import React from 'react';
import { motion } from 'framer-motion';
/**
 * SynonymAntonymPair — Two words side by side with = (synonym) or ≠ (antonym).
 *
 * Pairs with WOORD-SYNONIEM, WOORD-ANTONIEM, WOORD-CONTEXT families.
 *
 * Audit reference: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026.md V5
 */
export type WordRelation = 'synoniem' | 'antoniem';
interface SynonymAntonymPairProps {
  leftWord: string;
  rightWord: string;
  relation: WordRelation;
  /** Optional third word shown as a distractor in support context. */
  distractorWord?: string;
  label?: string;
  mode?: 'stimulus' | 'support';
}
const RELATION_STYLES: Record<WordRelation, {
  symbol: string;
  symbolLabel: string;
  ring: string;
  chipBg: string;
  chipText: string;
}> = {
  synoniem: {
    symbol: '=',
    symbolLabel: 'gelijk aan',
    ring: 'ring-emerald-300',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-800'
  },
  antoniem: {
    symbol: '↔',
    symbolLabel: 'tegenovergesteld aan',
    ring: 'ring-rose-300',
    chipBg: 'bg-rose-100',
    chipText: 'text-rose-800'
  }
};
export function SynonymAntonymPair({
  leftWord,
  rightWord,
  relation,
  distractorWord,
  label,
  mode = 'support'
}: SynonymAntonymPairProps) {
  // Defensive lookup: if a payload arrives with a `relation` value outside
  // 'synoniem' | 'antoniem' the original `RELATION_STYLES[relation]` returns
  // undefined and the JSX below crashes. Synoniem is the safer default.
  const style = RELATION_STYLES[relation] ?? RELATION_STYLES.synoniem;
  const isStimulus = mode === 'stimulus';
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className={`bg-white rounded-xl border border-slate-200 ${isStimulus ? 'p-3' : 'p-4'}`} role="group" aria-label={label || `${leftWord} ${style.symbolLabel} ${rightWord}`} data-id="element-13164">
      {label && <h4 className={`font-semibold text-slate-800 mb-2 ${isStimulus ? 'text-sm' : 'text-base'}`} data-id="element-13165">
          {label}
        </h4>}
      <div className="flex items-center justify-center gap-3 flex-wrap" data-id="element-13166">
        <span className={`inline-flex items-center justify-center rounded-lg font-bold bg-slate-100 text-slate-800 ring-2 ring-offset-1 ring-slate-300 ${isStimulus ? 'px-3 py-1.5 text-base' : 'px-4 py-2 text-lg'}`} data-id="element-13167">
          {leftWord}
        </span>
        <span aria-label={style.symbolLabel} className={`font-bold text-slate-500 ${isStimulus ? 'text-xl' : 'text-2xl'}`} data-id="element-13168">
          {style.symbol}
        </span>
        <span className={`inline-flex items-center justify-center rounded-lg font-bold ${style.chipBg} ${style.chipText} ring-2 ring-offset-1 ${style.ring} ${isStimulus ? 'px-3 py-1.5 text-base' : 'px-4 py-2 text-lg'}`} data-id="element-13169">
          {rightWord}
        </span>
        {distractorWord && <span className={`inline-flex items-center justify-center rounded-lg font-medium bg-slate-50 text-slate-500 line-through opacity-70 ${isStimulus ? 'px-3 py-1.5 text-base' : 'px-4 py-2 text-lg'}`} title="afleider" data-id="element-13170">
            {distractorWord}
          </span>}
      </div>
      <p className={`mt-3 text-center text-slate-400 font-medium ${isStimulus ? 'text-[10px]' : 'text-[11px]'}`} data-id="element-13171">
        {relation === 'synoniem' ? 'Synoniem (zelfde betekenis)' : 'Antoniem (tegenovergestelde betekenis)'}
      </p>
    </motion.div>;
}